import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoModule } from '@jsverse/transloco';
import { PdfPreviewerComponent } from '@modules/shared/pdf-previewer/pdf-previewer.component';
import { SpinnerComponent } from '@modules/shared/spinner/spinner.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DocumentTemplateType } from '@pixacare/domain';
import { DocumentTemplateResponse, Telemonitoring } from '@pixacare/dtos';
import { ReportApiService } from '@services/http/report.api.service';
import { TelemonitoringHttpService } from '@services/http/telemonitoring.http.service';
import { ReportTemplatePickerComponent } from '@shared/report-template-picker/report-template-picker.component';
import { toRequestResult } from '@shared/request-state/adapter/to-request-result';
import { RequestResult } from '@shared/request-state/models/request-result';
import { withRequestResult } from '@shared/request-state/rx-pipes/with-request-result.pipe';
import { getInitialRequestState } from '@shared/request-state/utils/get-initial-request-state';
import { downloadBlob } from '@shared/utils/utils';
import { TuiButton, TuiDialogContext, TuiTitle } from '@taiga-ui/core';
import { TuiBlockStatus } from '@taiga-ui/layout';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { Observable, of } from 'rxjs';
import { PdfResponse } from '@shared/models/pdf-response';

@Component({
  selector: 'app-telemonitoring-report',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    PdfPreviewerComponent,
    ReportTemplatePickerComponent,
    TranslocoDirective,
    TuiButton,
    TuiTitle,
    SpinnerComponent,
    TuiBlockStatus,
  ],
  templateUrl: './telemonitoring-report.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full flex-col justify-between gap-4 overflow-auto p-6' },
})
@UntilDestroy()
export class TelemonitoringReportComponent implements OnInit {
  private readonly telemonitoringService = inject(TelemonitoringHttpService);
  private readonly reportService = inject(ReportApiService);

  private readonly context = inject<
    TuiDialogContext<
      void,
      {
        clientCode: string;
        telemonitoring: Telemonitoring;
        title: string;
      }
    >
  >(POLYMORPHEUS_CONTEXT);

  @Input() clientCode: string = this.context.data?.clientCode;
  @Input() telemonitoring: Telemonitoring = this.context.data?.telemonitoring;
  @Input() title: string = this.context.data?.title;

  readonly templates$: Observable<DocumentTemplateResponse[]> = this.reportService.getTemplates(
    DocumentTemplateType.TELEMONITORING,
  );

  pdfPreview$: Observable<RequestResult<PdfResponse>> = of(toRequestResult(getInitialRequestState(null)));

  form = new FormGroup({
    templateId: new FormControl<number>(null, [Validators.required]),
  });

  ngOnInit(): void {
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.generatePdfPreview();
    });
  }

  close(): void {
    this.context.completeWith();
  }

  generatePdfPreview(): void {
    if (!this.form.valid) {
      return;
    }

    this.pdfPreview$ = this.telemonitoringService
      .report(this.clientCode, this.telemonitoring.id, this.form.getRawValue())
      .pipe(withRequestResult());
  }

  download(content: Blob): void {
    downloadBlob(content, `${this.title}.pdf`);
    this.close();
  }
}
